import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import ProductGradeBadge from './ProductGradeBadge';
import { useGradeSettings } from '@/hooks/useGradeSettings';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi
} from '@/components/ui/carousel';

interface GradeLegendProps {
  defaultGrade?: string;
  mode?: 'list' | 'carousel';
}

const GradeLegend = ({ defaultGrade, mode = 'list' }: GradeLegendProps) => {
  const { getGradeSettings } = useGradeSettings();
  const [api, setApi] = useState<CarouselApi>();

  const grades = [
    {
      grade: 'A' as const,
      title: 'פרימיום',
      defaultDescription: 'מוצר איכותי במצב מושלם, באריזה מקורית ובמצב חדש לחלוטין'
    },
    {
      grade: 'B' as const,
      title: 'סטנדרט',
      defaultDescription: 'מוצר במצב טוב, עשוי להיות ללא אריזה מקורית אך תקין לחלוטין'
    },
    {
      grade: 'C' as const,
      title: 'כפי שהוא',
      defaultDescription: 'מוצר נמכר במצבו הנוכחי, ללא אחריות או החזרה'
    },
    {
      grade: 'D' as const,
      title: 'מכירה סופית',
      defaultDescription: 'מכירה סופית, המוצר עשוי להיות עם פגמים קוסמטיים, ללא אחריות או החזרה'
    }
  ];

  useEffect(() => {
    if (api && mode === 'carousel' && defaultGrade) {
      // Use a small delay to ensure carousel is fully initialized and visible
      const timer = setTimeout(() => {
        const index = grades.findIndex(g => g.grade === defaultGrade);
        if (index !== -1) {
          api.scrollTo(index);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [api, defaultGrade, mode]);

  if (mode === 'carousel') {
    return (
      <Card className="overflow-hidden border-none shadow-none bg-transparent">
        <CardHeader className="px-0 pt-0">
          <CardTitle className="text-xl sm:text-2xl mb-2">דירוגי איכות</CardTitle>
          <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-foreground">
              <strong>לתשומת לבכם:</strong> כל המוצרים נבדקים על ידינו לפני המכירה. הדירוג מסייע לכם להבין את מצב המוצר ותנאי הרכישה.
            </p>
          </div>
        </CardHeader>
        <CardContent className="px-0">
          <Carousel
            setApi={setApi}
            className="w-full"
            opts={{
              direction: 'rtl',
              align: 'center',
              loop: true
            }}
          >
            <CarouselContent className="-ml-4">
              {grades.map((grade) => {
                const settings = getGradeSettings(grade.grade);
                const description = settings?.description || grade.defaultDescription;
                const isCurrent = defaultGrade === grade.grade;

                return (
                  <CarouselItem key={grade.grade} className="pl-4 md:basis-1/2 lg:basis-1/3">
                    <div className={`h-full p-4 sm:p-6 rounded-xl border transition-all duration-300 ${isCurrent
                      ? 'bg-primary/5 border-primary shadow-sm'
                      : 'bg-card border-border hover:border-primary/50'
                      }`}>
                      <div className="flex items-center gap-3 mb-4">
                        <ProductGradeBadge grade={grade.grade} size="md" />
                        <h3 className={`font-bold text-lg ${isCurrent ? 'text-primary' : 'text-foreground'}`}>
                          {grade.title}
                        </h3>
                      </div>
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        {description}
                      </p>
                    </div>
                  </CarouselItem>
                );
              })}
            </CarouselContent>
            <div className="flex gap-2 mt-4 justify-end">
              <CarouselNext className="static translate-y-0" />
              <CarouselPrevious className="static translate-y-0" />
            </div>
          </Carousel>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-2xl mb-2">דירוגי איכות</CardTitle>
        <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <p className="text-sm text-foreground">
            <strong>לתשומת לבכם:</strong> כל המוצרים נבדקים על ידינו לפני המכירה. הדירוג מסייע לכם להבין את מצב המוצר ותנאי הרכישה.
          </p>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {grades.map((grade) => {
            const settings = getGradeSettings(grade.grade);
            const description = settings?.description || grade.defaultDescription;

            return (
              <div key={grade.grade} className="flex items-start gap-4 p-4 rounded-lg bg-muted/30">
                <ProductGradeBadge grade={grade.grade} size="md" />
                <div className="flex-1">
                  <h3 className="font-semibold text-lg mb-1">{grade.title}</h3>
                  <p className="text-muted-foreground">{description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default GradeLegend;
